import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import { confirmDelete } from '../utils/confirmDelete';
import { T, PageHeader, Alert } from '../components/UI';

const PERMISSION_GROUPS = [
  {
    group: 'Daily Sheet',
    icon: 'ti-file-description',
    permissions: [
      { key:'canViewJobs',      label:'View Daily Sheet' },
      { key:'canCreateJobs',    label:'Create Jobs' },
      { key:'canEditJobs',      label:'Edit Jobs' },
      { key:'canDeleteJobs',    label:'Delete Jobs' },
      { key:'canUploadExcel',   label:'Upload Excel' },
      { key:'canDownloadExcel', label:'Download Excel' },
      { key:'canViewRevenue',   label:'View Price / Revenue' },
      { key:'canSearchJobs',    label:'Search Jobs (all records)' },
    ],
  },
  {
    group: 'Inspections',
    icon: 'ti-clipboard-check',
    permissions: [
      { key:'canViewInspections',   label:'View Inspections' },
      { key:'canCreateInspections', label:'Create Inspections' },
      { key:'canEditInspections',   label:'Edit Inspections' },
      { key:'canDeleteInspections', label:'Delete Inspections' },
      { key:'canReviewInspections', label:'Review Inspections' },
    ],
  },
  {
    group: 'HR',
    icon: 'ti-users',
    permissions: [
      { key:'canViewStaff',    label:'View Staff' },
      { key:'canManageStaff', label:'Manage Staff (Add/Edit/Delete)' },
      { key:'canViewReports', label:'View Reports' },
      { key:'canViewActivity', label:'View Activity Log' },
    ],
  },
  {
    group: 'Settings',
    icon: 'ti-settings',
    permissions: [
      { key:'canManageSettings', label:'Manage Settings (Dealerships, Locations, Services)' },
    ],
  },
];

const defaultPerms = Object.fromEntries(
  PERMISSION_GROUPS.flatMap(g => g.permissions.map(p => [p.key, false]))
);

export default function Roles() {
  const navigate = useNavigate();
  const [roles,    setRoles]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [error,    setError]    = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editing,  setEditing]  = useState(null);
  const [form,     setForm]     = useState({ name:'', label:'', permissions:{ ...defaultPerms } });
  const [saving,   setSaving]   = useState(false);
  const [deleting, setDeleting] = useState(null);

  const fetch = async () => {
    setLoading(true);
    try { const { data } = await api.get('/users/roles'); setRoles(data.roles||[]); }
    catch { setError('Failed to load roles'); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetch(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.label.trim()) return;
    setSaving(true); setError(null);
    try {
      if (editing) { await api.patch(`/users/roles/${editing._id}`, form); }
      else { await api.post('/users/roles', form); }
      setForm({ name:'', label:'', permissions:{ ...defaultPerms } });
      setShowForm(false); setEditing(null); fetch();
    } catch (err) { setError(err.response?.data?.message || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const startEdit = (role) => {
    setEditing(role);
    setForm({ name:role.name, label:role.label, permissions:{ ...defaultPerms, ...(role.permissions||{}) } });
    setShowForm(true);
    window.scrollTo({top:0, behavior:'smooth'});
  };

  const cancelForm = () => {
    setShowForm(false); setEditing(null);
    setForm({ name:'', label:'', permissions:{ ...defaultPerms } });
  };

  const handleDelete = async (id, label) => {
    if (!window.confirm(`Delete role "${label}"? Users with this role will lose access.`)) return;
    if (!await confirmDelete()) return;
    setDeleting(id);
    try { await api.delete(`/users/roles/${id}`); fetch(); }
    catch (err) { setError(err.response?.data?.message || 'Failed to delete'); }
    finally { setDeleting(null); }
  };

  const togglePerm = (key) => setForm(f => ({ ...f, permissions:{ ...f.permissions, [key]:!f.permissions[key] } }));

  const toggleGroup = (group) => {
    const keys = group.permissions.map(p=>p.key);
    const allOn = keys.every(k => form.permissions[k]);
    const newPerms = { ...form.permissions };
    keys.forEach(k => newPerms[k] = !allOn);
    setForm(f => ({ ...f, permissions: newPerms }));
  };

  const getPermCount = (role) => PERMISSION_GROUPS.flatMap(g=>g.permissions).filter(p=>role.permissions?.[p.key]).length;
  const totalPerms = PERMISSION_GROUPS.flatMap(g=>g.permissions).length;

  return (
    <div style={T.page}>
      <PageHeader title="Roles & Permissions" onBack={()=>navigate(-1)} actions={
        <button onClick={()=>{ cancelForm(); setShowForm(s=>!s); }} style={T.btnPrimary}>
          <i className="ti ti-plus" aria-hidden="true"/> Add Role
        </button>
      }/>
      <div style={{ maxWidth:'760px', margin:'0 auto', padding:'16px 16px 80px' }}>
        {error && <Alert type="error">{error}</Alert>}

        {/* Add/Edit Form */}
        {showForm && (
          <div style={{ background:'var(--surface)', border:`1px solid ${editing?'var(--amber)':'var(--blue)'}`, borderRadius:'var(--radius-lg)', padding:'20px', marginBottom:'16px' }}>
            <div style={{ fontSize:'14px', fontWeight:'500', color:editing?'var(--amber)':'var(--blue)', marginBottom:'16px' }}>
              {editing ? `Edit Role: ${editing.label}` : 'Create New Role'}
            </div>

            <form onSubmit={handleSubmit}>
              {/* Role name + label */}
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px', marginBottom:'20px' }}>
                <div>
                  <label style={T.label}>Role Key * <span style={{ color:'var(--hint)', textTransform:'none', letterSpacing:0 }}>(lowercase, no spaces)</span></label>
                  <input value={form.name}
                    onChange={e=>setForm(f=>({...f,name:e.target.value.toLowerCase().replace(/[^a-z0-9_]/g,'')}))}
                    style={T.input} placeholder="e.g. supervisor" required
                    disabled={!!editing}
                    onFocus={e=>e.target.style.borderColor='var(--blue)'}
                    onBlur={e=>e.target.style.borderColor='var(--border-md)'}/>
                </div>
                <div>
                  <label style={T.label}>Display Name *</label>
                  <input value={form.label} onChange={e=>setForm(f=>({...f,label:e.target.value}))}
                    style={T.input} placeholder="e.g. Supervisor" required
                    onFocus={e=>e.target.style.borderColor='var(--blue)'}
                    onBlur={e=>e.target.style.borderColor='var(--border-md)'}/>
                </div>
              </div>

              {/* Permissions — segmented by group */}
              <div style={{ marginBottom:'16px' }}>
                <label style={{ ...T.label, marginBottom:'12px' }}>Permissions</label>
                <div style={{ display:'flex', flexDirection:'column', gap:'12px' }}>
                  {PERMISSION_GROUPS.map(group => {
                    const keys = group.permissions.map(p=>p.key);
                    const allOn = keys.every(k=>form.permissions[k]);
                    const someOn = keys.some(k=>form.permissions[k]);
                    return (
                      <div key={group.group} style={{ border:'1px solid var(--border)', borderRadius:'var(--radius-lg)', overflow:'hidden' }}>
                        {/* Group header with select all */}
                        <div style={{ padding:'10px 14px', background:'var(--bg)', borderBottom:'1px solid var(--border)', display:'flex', alignItems:'center', gap:'10px', cursor:'pointer' }} onClick={()=>toggleGroup(group)}>
                          <i className={`ti ${group.icon}`} style={{ color:'var(--blue)', fontSize:'16px' }} aria-hidden="true"/>
                          <span style={{ flex:1, fontSize:'13px', fontWeight:'500', color:'var(--text)' }}>{group.group}</span>
                          <div style={{ width:'20px', height:'20px', border:`2px solid ${allOn?'var(--blue)':someOn?'var(--blue)':'var(--border-md)'}`, borderRadius:'4px', background:allOn?'var(--blue)':someOn?'var(--blue-light)':'transparent', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                            {allOn && <i className="ti ti-check" style={{ fontSize:'12px', color:'#fff' }} aria-hidden="true"/>}
                            {someOn && !allOn && <div style={{ width:'8px', height:'2px', background:'var(--blue)', borderRadius:'1px' }}/>}
                          </div>
                          <span style={{ fontSize:'11px', color:'var(--muted)' }}>
                            {keys.filter(k=>form.permissions[k]).length}/{keys.length}
                          </span>
                        </div>
                        {/* Permission checkboxes */}
                        <div style={{ padding:'10px 14px', display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(220px,1fr))', gap:'6px' }}>
                          {group.permissions.map(p=>(
                            <label key={p.key} onClick={()=>togglePerm(p.key)}
                              style={{ display:'flex', alignItems:'center', gap:'8px', padding:'8px 10px', background:form.permissions[p.key]?'var(--blue-light)':'var(--surface)', borderRadius:'var(--radius)', border:`1px solid ${form.permissions[p.key]?'var(--blue)':'var(--border)'}`, cursor:'pointer', fontSize:'13px', color:form.permissions[p.key]?'var(--blue)':'var(--muted)', transition:'all .15s' }}>
                              <div style={{ width:'18px', height:'18px', border:`2px solid ${form.permissions[p.key]?'var(--blue)':'var(--border-md)'}`, borderRadius:'4px', background:form.permissions[p.key]?'var(--blue)':'transparent', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                                {form.permissions[p.key] && <i className="ti ti-check" style={{ fontSize:'11px', color:'#fff' }} aria-hidden="true"/>}
                              </div>
                              {p.label}
                            </label>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div style={{ display:'flex', gap:'8px', justifyContent:'flex-end' }}>
                <button type="button" onClick={cancelForm} style={T.btnSecondary}>Cancel</button>
                <button type="submit" disabled={saving} style={{ ...T.btnPrimary, background:editing?'var(--amber)':'var(--blue)', opacity:saving?0.6:1 }}>
                  {saving ? 'Saving...' : editing ? 'Save Changes' : 'Create Role'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Roles list */}
        {loading ? (
          <div style={{ textAlign:'center', padding:'40px', color:'var(--muted)' }}>Loading...</div>
        ) : (
          <div style={{ display:'flex', flexDirection:'column', gap:'10px' }}>
            {roles.map(role=>(
              <div key={role._id} style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--radius-lg)', padding:'16px' }}>
                <div style={{ display:'flex', alignItems:'flex-start', gap:'12px' }}>
                  <div style={{ width:'38px', height:'38px', background:'var(--blue-light)', borderRadius:'var(--radius)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                    <i className="ti ti-shield" style={{ color:'var(--blue)', fontSize:'18px' }} aria-hidden="true"/>
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'8px', flexWrap:'wrap' }}>
                      <span style={{ fontWeight:'500', color:'var(--text)', fontSize:'14px' }}>{role.label}</span>
                      <span style={{ background:'var(--bg)', color:'var(--muted)', fontSize:'11px', fontFamily:'JetBrains Mono,monospace', padding:'2px 8px', borderRadius:'20px' }}>{role.name}</span>
                      {role.isSystem && <span style={{ background:'var(--blue-light)', color:'var(--blue)', fontSize:'11px', fontWeight:'500', padding:'2px 8px', borderRadius:'20px' }}>System</span>}
                      <span style={{ background:'var(--green-bg)', color:'var(--green)', fontSize:'11px', padding:'2px 8px', borderRadius:'20px' }}>
                        {getPermCount(role)}/{totalPerms} permissions
                      </span>
                    </div>
                    {/* Permission summary by group */}
                    <div style={{ display:'flex', flexDirection:'column', gap:'4px' }}>
                      {PERMISSION_GROUPS.map(group=>{
                        const enabled = group.permissions.filter(p=>role.permissions?.[p.key]);
                        if (enabled.length === 0) return null;
                        return (
                          <div key={group.group} style={{ display:'flex', alignItems:'flex-start', gap:'6px' }}>
                            <i className={`ti ${group.icon}`} style={{ color:'var(--muted)', fontSize:'12px', marginTop:'2px', flexShrink:0 }} aria-hidden="true"/>
                            <span style={{ fontSize:'11px', color:'var(--muted)' }}>
                              <strong style={{ color:'var(--text)' }}>{group.group}:</strong> {enabled.map(p=>p.label).join(', ')}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <div style={{ display:'flex', gap:'6px', flexShrink:0 }}>
                    <button onClick={()=>startEdit(role)} style={{ ...T.btnIcon, color:'var(--blue)', borderColor:'rgba(10,110,180,0.25)' }}>
                      <i className="ti ti-edit" style={{ fontSize:'14px' }} aria-label="Edit"/>
                    </button>
                    {!role.isSystem && (
                      <button onClick={()=>handleDelete(role._id, role.label)} disabled={deleting===role._id} style={{ ...T.btnIcon, color:'var(--red)', borderColor:'rgba(163,45,45,0.25)' }}>
                        <i className="ti ti-trash" style={{ fontSize:'14px' }} aria-label="Delete"/>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {roles.length===0 && (
              <div style={{ textAlign:'center', padding:'40px', color:'var(--muted)', background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--radius-lg)' }}>
                No roles created yet. Click Add Role to create your first role.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
